import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';

// Constants for localStorage keys
const ADMIN_TOKEN_KEY = 'admin_token';
const IS_ADMIN_IMPERSONATING_KEY = 'is_admin_impersonating';
const ADMIN_EMAIL_KEY = 'admin_email';

/**
 * Utility function to store the current admin token before login as user
 * @returns Promise<void>
 */
const storeAdminToken = async (adminToken: string): Promise<void> => {
  try {
    if (auth.currentUser) {
      localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
      localStorage.setItem(ADMIN_EMAIL_KEY, auth.currentUser.email || '');
    }
  } catch (error) {
    console.error('Error storing admin token:', error);
  }
};

/**
 * Utility function to login as a specific user using a custom token
 * This should only be used by admin users for debugging purposes
 * @param customToken - The custom token generated for the user
 * @returns Promise<void>
 */
export const loginAsUser = async (customToken: string, adminToken: string): Promise<void> => {
  try {
    // Store the current admin token and email
    await storeAdminToken(adminToken);
    
    // Sign out current user first
    await auth.signOut();
    
    // Sign in with the custom token
    await signInWithCustomToken(auth, customToken);
    
    // Set flag that we're impersonating a user
    localStorage.setItem(IS_ADMIN_IMPERSONATING_KEY, 'true');
    
    // Reload the page to update the UI
    window.location.href = '/';
  } catch (error) {
    console.error('Error logging in as user:', error);
    throw error;
  }
};

/**
 * Check if the current session is an admin impersonating a user
 * @returns boolean
 */
export const isAdminImpersonatingUser = (): boolean => {
  return localStorage.getItem(IS_ADMIN_IMPERSONATING_KEY) === 'true';
};

/**
 * Get the admin email that is currently impersonating a user
 * @returns string or null
 */
export const getAdminEmail = (): string | null => {
  return localStorage.getItem(ADMIN_EMAIL_KEY);
};

/**
 * Utility function to revert back to the admin account
 * @returns Promise<void>
 */
export const revertToAdmin = async (): Promise<void> => {
  try {
    // Get the admin token
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    
    if (!adminToken) {
      console.error('No admin token found');
      return;
    }
    
    // Sign out current user first
    await auth.signOut();
    
    // Sign in with the admin token
    await signInWithCustomToken(auth, adminToken);
    
    // Clear impersonation flags after successful sign-in
    localStorage.removeItem(IS_ADMIN_IMPERSONATING_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    
    // Redirect to admin users page
    window.location.href = '/admin';
  } catch (error) {
    console.error('Error reverting to admin:', error);
    
    // Clear impersonation flags on error
    localStorage.removeItem(IS_ADMIN_IMPERSONATING_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    
    // Redirect to login page on error
    window.location.href = '/login';
  }
};
